import { db } from './firebase';
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  type Unsubscribe,
} from 'firebase/firestore';
import type {
  MarketingActivityLevel,
  MarketingCompetitorDetail,
  MarketingOpportunityReport,
  MarketingOpportunityReportListItem,
  MarketingReport,
  MarketingReportListItem,
} from '../types';
import { firestoreSnapshotListenError } from './firestoreListenDebug';
import {
  buildOpportunityHeadline,
  normalizeOpportunityRadarPayload,
} from './marketingOpportunityNormalize';

const COLLECTION = 'marketingReports';
const OPPORTUNITY_COLLECTION = 'marketingOpportunityReports';

function toDate(v: unknown): Date {
  if (!v) return new Date(0);
  if (v instanceof Date) return v;
  if (typeof v === 'object' && v !== null && 'toDate' in v && typeof (v as { toDate: () => Date }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  if (typeof v === 'string' || typeof v === 'number') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? new Date(0) : d;
  }
  return new Date(0);
}

function parseActivityLevel(raw: unknown): MarketingActivityLevel {
  return raw === 'low' || raw === 'high' ? raw : 'medium';
}

function parseStringArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is string => typeof x === 'string' && !!x.trim()).map(x => x.trim());
}

function parseCompetitorDetails(raw: unknown): MarketingCompetitorDetail[] {
  if (!Array.isArray(raw)) return [];
  const out: MarketingCompetitorDetail[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const name = typeof o.name === 'string' ? o.name.trim() : '';
    if (!name) continue;
    out.push({
      ...o,
      name,
      has_updates: o.has_updates === true,
    });
  }
  return out;
}

function parseRecordArray(raw: unknown): Record<string, unknown>[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x));
}

function countCompetitorsWithUpdates(details: MarketingCompetitorDetail[]): number {
  return details.filter(d => d.has_updates).length;
}

function buildHeadline(d: Record<string, unknown>, details: MarketingCompetitorDetail[]): string {
  const highlights = parseStringArray(d.daily_highlights);
  if (highlights[0]) return highlights[0];
  const updated = details.find(x => x.has_updates);
  if (updated?.name) return `${updated.name} has updates`;
  return '';
}

export function fromFirestore(snap: { id: string; data: () => Record<string, unknown> }): MarketingReport {
  const d = snap.data();
  const competitor_details = parseCompetitorDetails(d.competitor_details);

  return {
    id: snap.id,
    report_date: (d.report_date as string) || snap.id,
    monitoring_period: (d.monitoring_period as string) || '',
    focus_competitors: parseStringArray(d.focus_competitors),
    overall_activity_level: parseActivityLevel(d.overall_activity_level),
    daily_highlights: parseStringArray(d.daily_highlights),
    seo_keywords_analysis:
      d.seo_keywords_analysis && typeof d.seo_keywords_analysis === 'object' && !Array.isArray(d.seo_keywords_analysis)
        ? (d.seo_keywords_analysis as Record<string, unknown>)
        : undefined,
    google_business_profile_monitoring:
      d.google_business_profile_monitoring &&
      typeof d.google_business_profile_monitoring === 'object' &&
      !Array.isArray(d.google_business_profile_monitoring)
        ? (d.google_business_profile_monitoring as Record<string, unknown>)
        : undefined,
    new_kits_released: parseRecordArray(d.new_kits_released),
    marketing_tone_style_feel:
      d.marketing_tone_style_feel &&
      typeof d.marketing_tone_style_feel === 'object' &&
      !Array.isArray(d.marketing_tone_style_feel)
        ? (d.marketing_tone_style_feel as Record<string, unknown>)
        : undefined,
    partnerships_observed: parseRecordArray(d.partnerships_observed),
    competitor_details,
    key_market_trends: typeof d.key_market_trends === 'string' ? d.key_market_trends : undefined,
    recommendations_for_leipers_creek: parseStringArray(d.recommendations_for_leipers_creek),
    data_notes: typeof d.data_notes === 'string' ? d.data_notes : undefined,
    source: 'marketing-agent',
    createdAt: toDate(d.createdAt),
    updatedAt: toDate(d.updatedAt),
  };
}

export function toListItem(snap: { id: string; data: () => Record<string, unknown> }): MarketingReportListItem {
  const d = snap.data();
  const competitor_details = parseCompetitorDetails(d.competitor_details);
  return {
    report_date: (d.report_date as string) || snap.id,
    overall_activity_level: parseActivityLevel(d.overall_activity_level),
    competitors_with_updates: countCompetitorsWithUpdates(competitor_details),
    headline: buildHeadline(d, competitor_details),
  };
}

/** Real-time list of recent marketing reports (newest first). */
export function subscribeMarketingReports(
  callback: (reports: MarketingReportListItem[]) => void,
  onError?: (e: Error) => void,
  maxItems = 30,
): Unsubscribe {
  const q = query(
    collection(db, COLLECTION),
    orderBy('report_date', 'desc'),
    limit(maxItems),
  );
  return onSnapshot(
    q,
    snap => {
      callback(snap.docs.map(d => toListItem({ id: d.id, data: () => d.data() })));
    },
    err => {
      firestoreSnapshotListenError('marketingReportsService', COLLECTION, err);
      onError?.(err as Error);
    },
  );
}

/** Real-time subscription to a single report by date (YYYY-MM-DD). */
export function subscribeMarketingReport(
  reportDate: string,
  callback: (report: MarketingReport | null) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  const ref = doc(db, COLLECTION, reportDate);
  return onSnapshot(
    ref,
    snap => {
      if (!snap.exists()) {
        callback(null);
        return;
      }
      callback(fromFirestore({ id: snap.id, data: () => snap.data() }));
    },
    err => {
      firestoreSnapshotListenError('marketingReportsService', `${COLLECTION}/${reportDate}`, err);
      onError?.(err as Error);
    },
  );
}

function opportunityToListItem(
  snap: { id: string; data: () => Record<string, unknown> },
): MarketingOpportunityReportListItem {
  const d = snap.data();
  const reportDate =
    (typeof d.report_date === 'string' && d.report_date.trim()) || snap.id;
  const normalized = normalizeOpportunityRadarPayload(d);
  return {
    id: snap.id,
    report_date: reportDate,
    opportunity_count: normalized.opportunities.length,
    headline:
      normalized.opportunities.length > 0
        ? buildOpportunityHeadline(d, normalized.opportunities)
        : 'Synced — check opportunity field shape',
    createdAt: toDate(d.createdAt),
    updatedAt: toDate(d.updatedAt),
  };
}

export function opportunityFromFirestore(
  snap: { id: string; data: () => Record<string, unknown> },
): MarketingOpportunityReport {
  const d = snap.data();
  const normalized = normalizeOpportunityRadarPayload(d);

  return {
    id: snap.id,
    report_date: (d.report_date as string) || snap.id,
    opportunities: normalized.opportunities,
    otherSignals: normalized.otherSignals,
    summary: normalized.summary,
    highlights: normalized.highlights,
    source: 'marketing-agent',
    createdAt: toDate(d.createdAt),
    updatedAt: toDate(d.updatedAt),
  };
}

function sortOpportunityReportRows(
  rows: MarketingOpportunityReportListItem[],
): MarketingOpportunityReportListItem[] {
  return [...rows].sort((a, b) => b.report_date.localeCompare(a.report_date));
}

function sortOpportunityReports(rows: MarketingOpportunityReport[]): MarketingOpportunityReport[] {
  return [...rows].sort((a, b) => {
    const au = a.updatedAt?.getTime() ?? 0;
    const bu = b.updatedAt?.getTime() ?? 0;
    if (bu !== au) return bu - au;
    return b.report_date.localeCompare(a.report_date) || (b.id ?? '').localeCompare(a.id ?? '');
  });
}

/** Real-time list of full opportunity radar documents (newest first). */
export function subscribeMarketingOpportunityReportDocs(
  callback: (reports: MarketingOpportunityReport[]) => void,
  onError?: (e: Error) => void,
  maxItems = 30,
): Unsubscribe {
  return onSnapshot(
    collection(db, OPPORTUNITY_COLLECTION),
    snap => {
      callback(
        sortOpportunityReports(
          snap.docs.map(d => opportunityFromFirestore({ id: d.id, data: () => d.data() })),
        ).slice(0, maxItems),
      );
    },
    err => {
      firestoreSnapshotListenError('marketingReportsService', OPPORTUNITY_COLLECTION, err);
      onError?.(err as Error);
    },
  );
}

/** Real-time list of opportunity radar reports from marketingOpportunityReports only. */
export function subscribeMarketingOpportunityReports(
  callback: (reports: MarketingOpportunityReportListItem[]) => void,
  onError?: (e: Error) => void,
  maxItems = 30,
): Unsubscribe {
  // Plain collection listener — avoids orderBy(documentId) / report_date index edge cases.
  // Sort client-side by report_date (falls back to doc id in opportunityToListItem).
  return onSnapshot(
    collection(db, OPPORTUNITY_COLLECTION),
    snap => {
      callback(
        sortOpportunityReportRows(
          snap.docs.map(d => opportunityToListItem({ id: d.id, data: () => d.data() })),
        ).slice(0, maxItems),
      );
    },
    err => {
      firestoreSnapshotListenError('marketingReportsService', OPPORTUNITY_COLLECTION, err);
      onError?.(err as Error);
    },
  );
}

/** Real-time subscription to a single opportunity radar report by date (YYYY-MM-DD). */
export function subscribeMarketingOpportunityReport(
  reportDate: string,
  callback: (report: MarketingOpportunityReport | null) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  const ref = doc(db, OPPORTUNITY_COLLECTION, reportDate);
  return onSnapshot(
    ref,
    snap => {
      if (!snap.exists()) {
        callback(null);
        return;
      }
      callback(opportunityFromFirestore({ id: snap.id, data: () => snap.data() }));
    },
    err => {
      firestoreSnapshotListenError('marketingReportsService', `${OPPORTUNITY_COLLECTION}/${reportDate}`, err);
      onError?.(err as Error);
    },
  );
}
